import React, { useState } from 'react';
import axios from 'axios';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Result, UploadFile } from 'antd';
import imageCompression from 'browser-image-compression';

import { useCreateUrlSigned } from '../../hooks/useCreateUrlSigned';
import { useGetRosaImages } from '../../hooks/useGetRosaImages';
import { CustomUpload, ImageSkeleton, List } from './styles';
import { CustomImage } from '../../components/CustomImage';
import { ROSA_QUERY_KEYS } from '../../hooks/query-keys';
import type { AntdCustomRequestOptions } from './types';
import { UploadFileButton } from './UploadFileButton';
import { useRosaContext } from '../../context';
import { queryClient } from '@/store/query';

const TIME_OUT = 1800000;
const MAX_FILE_SIZE = 10;
const MAX_UPLOAD_FILES = 3;
const MAX_FILE_DIMENSION = 600;

interface ImagesProps {
	onDelete(image: UploadFile): void;
	onDetails(image: UploadFile): void;
}

export function Images({ onDetails, onDelete }: Readonly<ImagesProps>) {
	const { organization_id, company_id, report_id } = useRosaContext();

	const [isUploading, setIsUploading] = useState<boolean>(false);

	const {
		isError,
		isFetching,
		data: images,
		isLoading: isGettingImages
	} = useGetRosaImages({ organization_id, company_id, report_id });
	const { mutateAsync: createUrlSigned } = useCreateUrlSigned();

	if (isGettingImages) {
		return <ImageSkeleton active />;
	}

	if (isError) {
		return (
			<Row justify="center">
				<Col>
					<Result
						status="error"
						title={I18n.get('Submission Failed')}
						subTitle={I18n.get('Please check and modify the following information before resubmitting.')}
					/>
				</Col>
			</Row>
		);
	}

	async function handleUploadImage(options: AntdCustomRequestOptions): Promise<void> {
		const { file } = options;

		setIsUploading(true);

		try {
			const compressedImage = await compressImage(file as File);

			const { url } = await createUrlSigned({
				company_id,
				organization_id,
				report_id,
				file_name: compressedImage.name,
				size_number: compressedImage.size,
				content_type: compressedImage.type
			});

			const headers = {
				onUploadProgress: () => {},
				timeout: TIME_OUT,
				headers: {
					'Content-Type': compressedImage.type
				}
			};

			await axios.put(url, compressedImage, headers);
			setIsUploading(false);

			await queryClient.invalidateQueries([ROSA_QUERY_KEYS.GET_ROSA_IMAGES]);
		} catch (error) {
			setIsUploading(false);
		}
	}

	async function compressImage(file: File): Promise<File> {
		const options = {
			useWebWorker: true,
			maxSizeMB: MAX_FILE_SIZE,
			maxWidthOrHeight: MAX_FILE_DIMENSION
		};

		const compressedImage = await imageCompression(file, options);
		return compressedImage;
	}

	function isImageListNotFull(): boolean {
		const totalImages = images?.length ?? 0;
		return totalImages < MAX_UPLOAD_FILES;
	}

	return (
		<List gutter={[20, 0]}>
			{images.map((image) => {
				return <CustomImage key={image.uid} image={image} onDetails={onDetails} onDelete={onDelete} />;
			})}
			{(isUploading || isFetching) && isImageListNotFull() && (
				<Col>
					<ImageSkeleton active />
				</Col>
			)}
			<Col>
				<CustomUpload
					showUploadList={false}
					listType="picture-card"
					accept=".jpg, .jpeg, .png"
					customRequest={handleUploadImage}
				>
					{isImageListNotFull() && !isUploading && !isFetching && <UploadFileButton />}
				</CustomUpload>
			</Col>
		</List>
	);
}
